/**
 * MailverlaufTab — Master-Detail-Ansicht pro Lead.
 *
 * Layout (Redesign 2026-06-19, Wunsch Matze):
 *  - Links (halbe Breite): kompakte Mail-Liste, neueste unten. Klick waehlt aus.
 *  - Rechts (halbe Breite): gewaehlte Mail mit viel Platz (Body + Anhaenge).
 *  - Kein globales Scrollen mehr — jede Spalte scrollt fuer sich.
 *
 *  - Inbound vs. Outbound visuell unterscheidbar (Farbe + Pfeil-Icon)
 *  - HTML-Bodies werden mit DOMPurify sanitized + im sandboxed iframe gerendert
 *  - Antworten-Button oeffnet MailComposeModal
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { ArrowDown, ArrowUp, Mail, RefreshCw, Reply, Loader2, Paperclip, Download } from 'lucide-react';
import { Button } from '@libero/core/components/ui/button';
import { leadsApi } from '../../services/leadsApi';
import type { Lead, LeadMail, LeadMailAttachment } from '../../types';
import MailComposeModal from '../MailComposeModal';

interface Props {
  lead: Lead;
}

// Anhaenge die im UI als Download-Chip erscheinen:
// echte Anhaenge IMMER, Inline-Bilder nur wenn sie groesser als eine
// Signatur-Grafik sind (eingebettete Logos sind winzig, gesendete Screenshots gross).
const INLINE_SIZE_THRESHOLD = 12_000;
function visibleAttachments(mail: LeadMail): LeadMailAttachment[] {
  return (mail.attachments ?? []).filter((a) => !a.isInline || a.size >= INLINE_SIZE_THRESHOLD);
}

export default function MailverlaufTab({ lead }: Props) {
  const [mails, setMails] = useState<LeadMail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  async function load(selectNewest = false) {
    setLoading(true);
    setError(null);
    try {
      const list = await leadsApi.mails(lead.id);
      setMails(list);
      setSelectedId((prev) => {
        if (selectNewest || prev == null) return list.length ? list[list.length - 1].id : null;
        // Auswahl behalten, falls die Mail noch existiert
        return list.some((m) => m.id === prev) ? prev : (list.length ? list[list.length - 1].id : null);
      });
    } catch (e: any) {
      setError(e.response?.data?.error?.message ?? 'Mailverlauf konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id]);

  const selected = useMemo(() => mails.find((m) => m.id === selectedId) ?? null, [mails, selectedId]);
  const lastInbound = [...mails].reverse().find((m) => m.direction === 'inbound');
  const lastSubject = lastInbound?.subject ?? '';

  if (loading && mails.length === 0) {
    return (
      <div className="py-8 px-4 text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Lade Mailverlauf...
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Kopfzeile */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#DAD9D9] shrink-0">
        <h2 className="text-base font-semibold text-[#1A1A1A]">
          Mailverlauf <span className="text-muted-foreground font-normal">({mails.length})</span>
        </h2>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => void load()}>
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Neu laden
          </Button>
          <Button
            size="sm"
            onClick={() => setComposeOpen(true)}
            className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
          >
            <Reply className="w-3.5 h-3.5 mr-1" /> Antworten
          </Button>
        </div>
      </div>

      {error && (
        <div className="m-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2 flex items-center gap-2 shrink-0">
          <span>{error}</span>
          <Button size="sm" variant="outline" onClick={() => void load()}>
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Erneut versuchen
          </Button>
        </div>
      )}

      {mails.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-sm text-muted-foreground">
          <Mail className="w-8 h-8 mb-2 opacity-40" />
          Noch keine Mails fuer diesen Lead
        </div>
      ) : (
        <div className="flex-1 flex min-h-0">
          {/* Liste links */}
          <div className="w-1/2 border-r border-[#DAD9D9] overflow-y-auto">
            {mails.map((m) => (
              <MailListItem
                key={m.id}
                mail={m}
                selected={m.id === selectedId}
                onSelect={() => setSelectedId(m.id)}
              />
            ))}
          </div>

          {/* Detail rechts */}
          <div className="w-1/2 overflow-y-auto bg-white">
            {selected ? (
              <MailDetail mail={selected} />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                Mail links auswaehlen
              </div>
            )}
          </div>
        </div>
      )}

      {composeOpen && (
        <MailComposeModal
          lead={lead}
          isReply
          replyToSubject={lastSubject}
          replyToMessageId={lastInbound?.messageId ?? null}
          onClose={() => setComposeOpen(false)}
          onSent={() => {
            setComposeOpen(false);
            void load(true);
          }}
        />
      )}
    </div>
  );
}

// ── Listen-Eintrag (links) ──────────────────────────────────────────────────

function MailListItem({
  mail,
  selected,
  onSelect,
}: {
  mail: LeadMail;
  selected: boolean;
  onSelect: () => void;
}) {
  const isInbound = mail.direction === 'inbound';
  const attCount = visibleAttachments(mail).length;
  const peer = isInbound ? mail.fromAddress : mail.toAddress;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left px-3 py-2.5 border-b border-[#DAD9D9]/60 transition-colors ${
        selected ? 'bg-[#E9FF57]/40 border-l-4 border-l-[#1A1A1A]' : 'hover:bg-[#DAD9D9]/30 border-l-4 border-l-transparent'
      }`}
    >
      <div className="flex items-center gap-1.5 text-xs text-[#1A1A1A] mb-0.5">
        {isInbound ? (
          <span className="flex items-center gap-1 font-semibold"><ArrowDown className="w-3 h-3" /> Eingang</span>
        ) : (
          <span className="flex items-center gap-1 font-semibold"><ArrowUp className="w-3 h-3" /> Gesendet</span>
        )}
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">{formatDateTime(mail.sentAt)}</span>
        {attCount > 0 && (
          <span className="ml-auto flex items-center gap-0.5 text-muted-foreground" title={`${attCount} Anhang`}>
            <Paperclip className="w-3 h-3" /> {attCount}
          </span>
        )}
      </div>
      <div className="text-xs text-muted-foreground truncate">{peer}</div>
      <div className="text-sm font-medium text-[#1A1A1A] truncate">{mail.subject || '(kein Betreff)'}</div>
    </button>
  );
}

// ── Detail (rechts) ──────────────────────────────────────────────────────────

function MailDetail({ mail }: { mail: LeadMail }) {
  const isInbound = mail.direction === 'inbound';
  const attachments = visibleAttachments(mail);

  return (
    <div className="p-5">
      <div className="flex items-center gap-2 text-xs text-[#1A1A1A] mb-1">
        {isInbound ? (
          <span className="flex items-center gap-1 font-semibold"><ArrowDown className="w-3.5 h-3.5" /> Eingang</span>
        ) : (
          <span className="flex items-center gap-1 font-semibold"><ArrowUp className="w-3.5 h-3.5" /> Gesendet</span>
        )}
        <span className="text-muted-foreground">·</span>
        <span>{formatDateTime(mail.sentAt)}</span>
      </div>
      <div className="text-xs text-muted-foreground mb-2">
        {mail.fromAddress} <span className="text-muted-foreground">→</span> {mail.toAddress}
      </div>
      <h3 className="text-lg font-semibold text-[#1A1A1A] mb-3">{mail.subject || '(kein Betreff)'}</h3>

      {attachments.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {attachments.map((att) => (
            <AttachmentChip key={att.id} leadId={mail.leadId} mailId={mail.id} att={att} />
          ))}
        </div>
      )}

      <MailBody mail={mail} />
    </div>
  );
}

// ── Anhang-Download-Chip ─────────────────────────────────────────────────────

function AttachmentChip({
  leadId,
  mailId,
  att,
}: {
  leadId: number;
  mailId: number;
  att: LeadMailAttachment;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  async function handleDownload() {
    setBusy(true);
    setErr(false);
    try {
      await leadsApi.downloadAttachment(leadId, mailId, att);
    } catch {
      setErr(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={() => void handleDownload()}
      disabled={busy}
      title={`${att.filename} herunterladen`}
      className={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-xs text-[#1A1A1A] bg-white hover:bg-[#E9FF57]/30 transition-colors disabled:opacity-60 ${
        err ? 'border-red-300' : 'border-[#DAD9D9]'
      }`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
      <span className="max-w-[260px] truncate font-medium">{att.filename}</span>
      <span className="text-muted-foreground">({formatBytes(att.size)})</span>
      <Download className="w-3.5 h-3.5" />
    </button>
  );
}

// ── Body (DOMPurify + sandboxed iframe) ─────────────────────────────────────

function MailBody({ mail }: { mail: LeadMail }) {
  if (mail.bodyHtml) {
    return <MailHtmlIframe html={mail.bodyHtml} />;
  }
  if (mail.bodyText) {
    return (
      <pre className="text-sm whitespace-pre-wrap bg-[#DAD9D9]/20 rounded p-3 font-sans text-[#1A1A1A]">
        {mail.bodyText}
      </pre>
    );
  }
  return <div className="text-sm text-muted-foreground italic">(Kein Inhalt)</div>;
}

/**
 * HTML-Mail im sandboxed iframe (CSS-Isolation + JS deaktiviert).
 * Hoehe waechst dynamisch; die Detail-Spalte scrollt bei sehr langen Mails.
 */
function MailHtmlIframe({ html }: { html: string }) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState<number>(200);

  const sanitized = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['target'],
  });

  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>
    html,body{margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;color:#1A1A1A;line-height:1.5;background:transparent;word-wrap:break-word}
    a{color:#1A1A1A;text-decoration:underline}
    img{max-width:100%;height:auto}
    table{max-width:100%}
  </style></head><body>${sanitized}</body></html>`;

  function handleLoad() {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument) return;
    try {
      const docHeight = Math.min(iframe.contentDocument.documentElement.scrollHeight + 16, 4000);
      setHeight(Math.max(docHeight, 120));
    } catch {
      setHeight(400);
    }
  }

  return (
    <iframe
      ref={iframeRef}
      title="Mail-Inhalt"
      srcDoc={srcDoc}
      sandbox=""
      onLoad={handleLoad}
      className="w-full bg-transparent border-0"
      style={{ height: `${height}px` }}
    />
  );
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const val = bytes / Math.pow(1024, i);
  return `${i === 0 ? val : val.toFixed(1)} ${units[i]}`;
}
